"use client";

import { useState } from "react";
import { ChevronDown, Eye } from "lucide-react";

import type { OpenEndedQuestion } from "./types";
import { renderInlineMath } from "./render-math";
import { event } from "@/lib/gtag";
import type { Locale } from "@/lib/i18n/locales";
import { quizStepCopy } from "@/lib/i18n/quiz-step-copy";
import { formatQuizRunner } from "@/lib/i18n/quiz-runner-copy";

type StepRevealProps = {
  question: OpenEndedQuestion;
  index: number;
  onInteract?: () => void;
  locale?: Locale;
};

export default function StepReveal({
  question,
  index,
  onInteract,
  locale = "en",
}: StepRevealProps) {
  const copy = quizStepCopy(locale);
  const [revealedCount, setRevealedCount] = useState(0);
  const totalSteps = question.markingPoints.length;
  const allRevealed = revealedCount >= totalSteps;

  const revealNext = () => {
    onInteract?.();
    const next = Math.min(revealedCount + 1, totalSteps);
    setRevealedCount(next);
    event("quiz_step_reveal", {
      question_id: question.id,
      step: next,
      total_steps: totalSteps,
    });
  };

  const revealAll = () => {
    onInteract?.();
    setRevealedCount(totalSteps);
    event("quiz_step_reveal", {
      question_id: question.id,
      step: totalSteps,
      total_steps: totalSteps,
      action: "show_all",
    });
  };

  const reset = () => {
    setRevealedCount(0);
    event("quiz_step_reveal", {
      question_id: question.id,
      step: 0,
      total_steps: totalSteps,
      action: "hide_all",
    });
  };

  return (
    <article className="rounded-xl bg-white p-6 shadow">
      <h3 className="mb-3 text-lg font-semibold text-gray-900">
        {formatQuizRunner(copy.question, { number: index + 1 })}{" "}
        {renderInlineMath(question.question, `step-question-${question.id}`)}
      </h3>

      <p className="mb-4 text-sm text-slate-500">{copy.instruction}</p>
      {locale !== "en" &&
        question.markingPoints.some((point) => point.includes("\\(")) && (
          <p className="mb-4 text-sm text-slate-600">{copy.mathScroll}</p>
        )}

      <div className="space-y-2">
        {question.markingPoints.map((point, stepIndex) => {
          const isRevealed = stepIndex < revealedCount;
          return (
            <div
              key={`${question.id}-step-${stepIndex}`}
              className={`rounded-lg border-2 px-4 py-3 text-sm transition-all ${
                isRevealed
                  ? "border-green-200 bg-green-50 text-green-900"
                  : "border-dashed border-slate-200 bg-slate-50 text-slate-400"
              }`}
            >
              <div className="flex items-start gap-2">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600">
                  {stepIndex + 1}
                </span>
                {isRevealed ? (
                  <span
                    className={
                      locale === "en"
                        ? "leading-relaxed"
                        : "min-w-0 flex-1 leading-relaxed"
                    }
                  >
                    {renderInlineMath(
                      point,
                      `step-point-${question.id}-${stepIndex}`,
                      locale === "en" ? undefined : copy.mathScroll
                    )}
                  </span>
                ) : (
                  <span className="select-none italic">
                    {formatQuizRunner(copy.hidden, { number: stepIndex + 1 })}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {!allRevealed ? (
          <>
            <button
              type="button"
              onClick={revealNext}
              className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
            >
              <ChevronDown className="h-4 w-4" />
              {formatQuizRunner(copy.next, {
                revealed: revealedCount,
                total: totalSteps,
              })}
            </button>
            <button
              type="button"
              onClick={revealAll}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              <Eye className="h-4 w-4" />
              {copy.all}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
          >
            {copy.hide}
          </button>
        )}
      </div>
    </article>
  );
}
